import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
export const operationStorageKey = (areaId, screenId) => `screen-operation:${areaId}:${screenId}`;
export default function useScreenOperation(areaId, screenId, { api = axios, basePath, storagePrefix = '' } = {}) {
  const [operation, setOperation] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(null);
  const generation = useRef(0);
  const timer = useRef(null);
  const inFlight = useRef(false);
  const reading = useRef(null);
  const base = basePath || `/api/areas/${areaId}/topology/screens/${screenId}/operations`;
  const storageKey = storagePrefix + operationStorageKey(areaId, screenId);
  const persist = useCallback((value) => {
    setPending(value);
    try { sessionStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* Storage may be disabled. */ }
  }, [storageKey]);

  const poll = useCallback(async function read(id, version) {
    if (reading.current === version) return;
    reading.current = version;
    clearTimeout(timer.current);
    try {
      const response = await api.get(`${base}/${id}`, { headers: headers(), timeout: 10000 });
      if (generation.current !== version) return;
      setOperation(response.data);
      setError('');
      if (response.data.status === 'running' && !response.data.stale) {
        timer.current = setTimeout(() => read(id, version), 1500);
      }
    } catch (failure) {
      if (generation.current === version) setError(`Could not read progress: ${failure.response?.data?.message || failure.message}. Check the existing request; do not send another toggle.`);
    } finally { if (reading.current === version) reading.current = null; }
  }, [base, api]);

  useEffect(() => {
    const version = ++generation.current;
    setOperation(null); setError(''); setSending(false); setPending(null);
    inFlight.current = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (saved) {
        setPending(saved);
        if (saved.id) poll(saved.id, version);
        else setError('A previous request has no response yet. Check that request before starting another routine.');
      }
    } catch { /* Ignore unavailable storage. */ }
    return () => { generation.current += 1; clearTimeout(timer.current); };
  }, [storageKey, poll]);

  const submit = useCallback(async (kind, retry = false, sourceDeviceId) => {
    if (inFlight.current || !areaId || !screenId) return;
    inFlight.current = true;
    // A response from the previous operation must not overwrite a new attempt.
    const version = ++generation.current;
    reading.current = null;
    const request = retry && pending ? pending : { operation: kind, ...(sourceDeviceId ? { sourceDeviceId } : {}),
      key: globalThis.crypto?.randomUUID?.() || `screen-${Date.now()}-${Math.random().toString(36).slice(2)}` };
    persist(request);
    setOperation(null);
    setSending(true); setError(''); clearTimeout(timer.current);
    try {
      const response = await api.post(`${base}/execute`, { operation: request.operation, idempotencyKey: request.key,
        ...(request.sourceDeviceId ? { sourceDeviceId: request.sourceDeviceId } : {}) },
        { headers: headers(), timeout: 15000 });
      if (generation.current !== version) return;
      persist({ ...request, id: response.data.id });
      setOperation(response.data);
      if (response.data.status === 'running') poll(response.data.id, version);
      return response.data;
    } catch (failure) {
      if (generation.current !== version) return;
      if ([400, 401, 403, 404, 409].includes(failure.response?.status || failure.status)) {
        setPending(null);
        try { sessionStorage.removeItem(storageKey); } catch { /* Storage may be disabled. */ }
      }
      setError(`${failure.response?.data?.message || failure.message}. Use “Check existing request” if delivery is uncertain.`);
    } finally { if (generation.current === version) { inFlight.current = false; setSending(false); } }
  }, [areaId, screenId, base, pending, persist, poll, storageKey, api]);
  const review = useCallback(async (stepKey, outcome, notes = '') => {
    if (!operation?.id) return;
    const response = await api.post(`${base}/${operation.id}/review`, { stepKey, outcome, notes }, { headers: headers() });
    setOperation(response.data);
    return response.data;
  }, [api, base, operation?.id]);
  const unresolved = Boolean(pending && !operation);
  return { operation, error, busy: sending || operation?.status === 'running' || unresolved,
    run: (kind, sourceDeviceId) => submit(kind, false, sourceDeviceId),
    review,
    check: () => pending?.id && !operation?.stale ? poll(pending.id, generation.current) : submit(pending?.operation, true),
    dismiss: () => {
      if (!operation || operation.status === 'running' || sending) return;
      generation.current += 1;
      clearTimeout(timer.current);
      reading.current = null;
      setOperation(null); setPending(null); setError('');
      try { sessionStorage.removeItem(storageKey); } catch { /* Storage may be disabled. */ }
    },
    canCheck: Boolean(pending && (!operation || operation.status === 'running')),
  };
}
